/**
 * Custom Patch upsell — adds main product + selected upsell via Cart AJAX API.
 * Preserves line item properties from the main product form (including app fields).
 *
 * Auto-suggest loads products from /recommendations/products.json (intent=related).
 */

const PATCH_UPSELL_CHECK_ICON = `<svg role="presentation" focusable="false" stroke-width="2" width="16" height="16" viewBox="0 0 18 18"><path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path><path d="M5 8.8L7.62937 11.6L13 6" stroke="#ffffff" fill="none"></path></svg>`;

class PatchUpsell extends HTMLElement {
  constructor() {
    super();
    this._onSubmit = this._onSubmit.bind(this);
    this._onVariantSelectChange = this._onVariantSelectChange.bind(this);
  }

  connectedCallback() {
    this._form = document.getElementById(this.getAttribute('form'));
    if (!this._form) return;

    this._form.addEventListener('submit', this._onSubmit, { capture: true });
    this.addEventListener('change', this._onVariantSelectChange);
    this.addEventListener('click', this._onPointerInteraction);
    this.addEventListener('mousedown', this._onPointerInteraction);

    if (this.hasAttribute('data-collection-source')) {
      this._loadCollectionProducts();
    } else if (this.hasAttribute('data-auto-suggest')) {
      this._loadRecommendations();
    }
  }

  disconnectedCallback() {
    this._form?.removeEventListener('submit', this._onSubmit, { capture: true });
    this.removeEventListener('change', this._onVariantSelectChange);
    this.removeEventListener('click', this._onPointerInteraction);
    this.removeEventListener('mousedown', this._onPointerInteraction);
  }

  _escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Normalize price to cents.
   * Recommendations API returns cents (number); collection products.json returns dollar strings ("18.00").
   */
  _toCents(price) {
    if (price == null || price === '') return 0;
    if (typeof price === 'string') {
      return Math.round(parseFloat(price) * 100) || 0;
    }
    return Number(price) || 0;
  }

  _formatMoney(cents) {
    const amount = this._toCents(cents);
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat || settings.moneyFormat
      : settings.moneyFormat;

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amount, format || '${{amount}}');
    }

    // Theme does not load Shopify.formatMoney — apply money format locally.
    const formatString = format || '${{amount}}';
    const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || 'amount';

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] != null ? `${dollars}${decimal}${parts[1]}` : dollars;
    };

    let value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amount, 2, "'");
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(amount, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(amount, 2, ' ');
        break;
      default:
        value = formatWithDelimiters(amount, 2);
    }

    return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  _getFirstAvailableVariant(product) {
    const available = product.variants?.find((variant) => variant.available);
    return available || product.variants?.[0] || null;
  }

  _getProductImage(product) {
    if (typeof product.featured_image === 'string') {
      return product.featured_image;
    }

    if (product.featured_image?.src) {
      return product.featured_image.src;
    }

    const firstImage = product.images?.[0];

    if (typeof firstImage === 'string') {
      return firstImage;
    }

    return firstImage?.src || null;
  }

  _renderPrice(variant) {
    const price = this._toCents(variant.price);
    const compareAtPrice = variant.compare_at_price ? this._toCents(variant.compare_at_price) : null;
    let html = `<span class="money">${this._formatMoney(price)}</span>`;

    if (compareAtPrice && compareAtPrice > price) {
      html = `<span class="money compare-at">${this._formatMoney(compareAtPrice)}</span>${html}`;
    }

    return `<div class="patch-upsell__card-price" data-spark="b2c-only"><span class="price-list price-list--sm">${html}</span></div>`;
  }

  _renderSparkPrice(productId) {
    if (!productId) return '';
    return `<spark-product-price class="patch-upsell__spark-price" parent-id="${productId}"></spark-product-price>`;
  }

  _renderVariantSelect(product, variant, blockId) {
    if (!product.variants || product.variants.length <= 1) return '';

    const variantLabel = this.getAttribute('data-variant-label') || 'Variant';
    const options = product.variants
      .map((item) => {
        const selected = item.id === variant.id ? ' selected' : '';
        const disabled = item.available ? '' : ' disabled';
        const price = this._toCents(item.price);
        const compareAtPrice = item.compare_at_price ? this._toCents(item.compare_at_price) : null;
        const compareAt = compareAtPrice ? ` data-compare-at-price="${compareAtPrice}"` : '';
        const sku = item.sku ? ` data-sku="${this._escapeHtml(item.sku)}"` : '';
        return `<option value="${item.id}" data-price="${price}"${compareAt}${sku}${selected}${disabled}>${this._escapeHtml(item.title)}</option>`;
      })
      .join('');

    const selectDisabled = variant.available ? '' : ' disabled';

    return `<select id="patch-upsell-variant-${blockId}-${product.id}" class="select text-sm patch-upsell__variant-select" data-patch-upsell-variant-select aria-label="${this._escapeHtml(variantLabel)} — ${this._escapeHtml(product.title)}"${selectDisabled}>${options}</select>`;
  }

  _renderProductOption(product, index) {
    const variant = this._getFirstAvailableVariant(product);
    if (!variant) return '';

    const blockId = this.getAttribute('data-block-id') || 'patch-upsell';
    const addOnLabel = this.getAttribute('data-add-on-label') || 'Add-on';
    const showNoneOption = this.getAttribute('data-show-none-option') !== 'false';
    const isUnavailable = !variant.available;
    const isFirstChecked = !showNoneOption && index === 0;
    const imageSrc = this._getProductImage(product);

    const imageHtml = imageSrc
      ? `<img src="${this._escapeHtml(imageSrc)}" alt="" class="patch-upsell__card-image rounded-xs" loading="lazy" width="52" height="52">`
      : '<div class="patch-upsell__card-placeholder rounded-xs" aria-hidden="true"></div>';

    return `
      <label class="patch-upsell__option${isUnavailable ? ' is-unavailable' : ''}" data-patch-upsell-item>
        <input
          type="radio"
          class="sr-only"
          name="patch-upsell-selection"
          value="${variant.id}"
          data-patch-upsell-radio
          tabindex="-1"
          data-product-id="${product.id}"
          data-sku="${this._escapeHtml(variant.sku || '')}"
          ${isFirstChecked ? 'checked' : ''}
          ${isUnavailable ? 'disabled' : ''}
        >
        <article class="patch-upsell__card">
          <span class="patch-upsell__card-badge">${this._escapeHtml(addOnLabel)}</span>
          <span class="patch-upsell__card-check" aria-hidden="true">${PATCH_UPSELL_CHECK_ICON}</span>
          <div class="patch-upsell__card-inner">
            ${imageHtml}
            <div class="patch-upsell__card-body">
              <p class="patch-upsell__card-title">${this._escapeHtml(product.title)}</p>
              ${this._renderPrice(variant)}
              ${this._renderSparkPrice(product.id)}
              ${this._renderVariantSelect(product, variant, blockId)}
            </div>
          </div>
        </article>
      </label>
    `;
  }

  async _loadRecommendations() {
    const productId = this.getAttribute('data-product-id');
    const limit = this.getAttribute('data-recommendations-limit') || '2';
    const intent = this.getAttribute('data-recommendations-intent') || 'related';
    const list = this.querySelector('[data-patch-upsell-list]');

    if (!productId || !list) {
      this.remove();
      return;
    }

    try {
      const url = `${Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      const response = await fetch(url, { priority: 'low' });

      if (!response.ok) {
        this.remove();
        return;
      }

      const data = await response.json();
      const products = data.products || [];

      if (products.length === 0) {
        this.remove();
        return;
      }

      list.innerHTML = products.slice(0, parseInt(limit, 10)).map((product, index) => this._renderProductOption(product, index)).join('');
      this.removeAttribute('hidden');
    } catch (error) {
      console.error('Patch upsell recommendations failed:', error);
      this.remove();
    }
  }

  async _loadCollectionProducts() {
    const collectionHandle = this.getAttribute('data-collection-source');
    const currentProductId = String(this.getAttribute('data-product-id') || '');
    const limit = Math.max(parseInt(this.getAttribute('data-recommendations-limit') || '2', 10), 1);
    const list = this.querySelector('[data-patch-upsell-list]');

    if (!collectionHandle || !list) {
      this.remove();
      return;
    }

    try {
      const url = `${Shopify.routes.root}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`;
      const response = await fetch(url, { priority: 'low' });

      if (!response.ok) {
        throw new Error(`Collection request failed with ${response.status}`);
      }

      const data = await response.json();
      const products = (data.products || []).filter((item) => {
        return String(item.id) !== currentProductId && item.variants?.some((variant) => variant.available);
      });

      if (products.length === 0) {
        this.remove();
        return;
      }

      // Stable per product page: different patches get different hats without changing on refresh.
      const seed = Array.from(currentProductId).reduce((total, character) => {
        return (total * 31 + character.charCodeAt(0)) >>> 0;
      }, 0);
      const startIndex = seed % products.length;
      const selectedProducts = Array.from(
        { length: Math.min(limit, products.length) },
        (_, index) => products[(startIndex + index) % products.length]
      );

      list.innerHTML = selectedProducts.map((item, index) => this._renderProductOption(item, index)).join('');
      this.removeAttribute('hidden');
    } catch (error) {
      console.error('Patch upsell collection load failed:', error);
      this.remove();
    }
  }

  _onPointerInteraction(event) {
    if (event.target.closest('[data-patch-upsell-variant-select]')) {
      event.stopPropagation();
      return;
    }

    const option = event.target.closest('.patch-upsell__option');
    if (!option) return;

    if (event.type === 'mousedown') {
      event.preventDefault();
      return;
    }

    if (event.type !== 'click') return;

    const radio = option.querySelector('[data-patch-upsell-radio]');
    if (!radio || radio.disabled) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _getSelectedUpsellVariantId() {
    const selected = this.querySelector('[data-patch-upsell-radio]:checked');
    if (!selected || !selected.value) return null;
    return selected.value;
  }

  _onVariantSelectChange(event) {
    const select = event.target.closest('[data-patch-upsell-variant-select]');
    if (!select) return;

    const label = select.closest('.patch-upsell__option');
    const radio = label?.querySelector('[data-patch-upsell-radio]');
    if (!radio) return;

    const selectedVariant = select.selectedOptions[0];
    if (!selectedVariant) return;

    radio.value = select.value;
    radio.dataset.sku = selectedVariant.dataset.sku || '';
    radio.disabled = selectedVariant.disabled;
    label.classList.toggle('is-unavailable', selectedVariant.disabled);
    radio.checked = true;

    const priceEl = label.querySelector('.patch-upsell__card-price');
    if (priceEl) {
      const variant = {
        price: parseInt(selectedVariant.dataset.price || '0', 10),
        compare_at_price: selectedVariant.dataset.compareAtPrice
          ? parseInt(selectedVariant.dataset.compareAtPrice, 10)
          : null
      };
      priceEl.outerHTML = this._renderPrice(variant);
    }

    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _formDataHasFiles(formData) {
    for (const value of formData.values()) {
      if (value instanceof File && value.size > 0) return true;
    }
    return false;
  }

  _extractProperties(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value instanceof File) continue;
      properties[match[1]] = value;
    }

    return properties;
  }

  _appendPropertiesToFormData(formData, index, properties, fileProperties) {
    for (const [name, value] of Object.entries(properties)) {
      formData.append(`items[${index}][properties][${name}]`, value);
    }

    for (const [name, file] of Object.entries(fileProperties)) {
      formData.append(`items[${index}][properties][${name}]`, file);
    }
  }

  _buildMultiItemFormData(originalFormData, mainVariantId, quantity, upsellVariantId, sectionsToBundle) {
    const formData = new FormData();
    const properties = {};
    const fileProperties = {};

    for (const [key, value] of originalFormData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match) continue;

      if (value instanceof File && value.size > 0) {
        fileProperties[match[1]] = value;
      } else if (!(value instanceof File)) {
        properties[match[1]] = value;
      }
    }

    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', `${Shopify.routes.root}variants/${mainVariantId}`);
    formData.set('items[0][id]', mainVariantId);
    formData.set('items[0][quantity]', quantity);
    this._appendPropertiesToFormData(formData, 0, properties, fileProperties);

    const sellingPlan = originalFormData.get('selling_plan');
    if (sellingPlan) {
      formData.set('items[0][selling_plan]', sellingPlan);
    }

    formData.set('items[1][id]', upsellVariantId);
    formData.set('items[1][quantity]', '1');

    return formData;
  }

  _buildMultiItemJson(originalFormData, mainVariantId, quantity, upsellVariantId, sectionsToBundle) {
    const properties = this._extractProperties(originalFormData);
    const mainItem = {
      id: parseInt(mainVariantId, 10),
      quantity: parseInt(quantity, 10) || 1
    };

    if (Object.keys(properties).length > 0) {
      mainItem.properties = properties;
    }

    const sellingPlan = originalFormData.get('selling_plan');
    if (sellingPlan) {
      mainItem.selling_plan = parseInt(sellingPlan, 10);
    }

    return {
      items: [
        mainItem,
        {
          id: parseInt(upsellVariantId, 10),
          quantity: 1
        }
      ],
      sections: sectionsToBundle.join(','),
      sections_url: `${Shopify.routes.root}variants/${mainVariantId}`
    };
  }

  async _handleCartResponse(response, submitButtons) {
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-busy');
    });

    const responseJson = await response.json();

    if (response.ok) {
      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this._form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson],
            cart: cartContent
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        })
      );
    } else {
      this._form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: responseJson.description
          }
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  async _onSubmit(event) {
    const upsellVariantId = this._getSelectedUpsellVariantId();
    if (!upsellVariantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this._form.checkValidity()) {
      this._form.reportValidity();
      return;
    }

    const submitButtons = Array.from(this._form.elements).filter((button) => button.type === 'submit');
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-busy', 'true');
    });

    try {
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const originalFormData = new FormData(this._form);
      const mainVariantId = originalFormData.get('id');
      const quantity = originalFormData.get('quantity') || '1';
      const hasFiles = this._formDataHasFiles(originalFormData);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: hasFiles
          ? { 'X-Requested-With': 'XMLHttpRequest' }
          : {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
        body: hasFiles
          ? this._buildMultiItemFormData(originalFormData, mainVariantId, quantity, upsellVariantId, sectionsToBundle)
          : JSON.stringify(
              this._buildMultiItemJson(originalFormData, mainVariantId, quantity, upsellVariantId, sectionsToBundle)
            )
      });

      await this._handleCartResponse(response, submitButtons);
    } catch (error) {
      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
      });
      console.error('Patch upsell add to cart failed:', error);
    }
  }
}

if (!window.customElements.get('patch-upsell')) {
  window.customElements.define('patch-upsell', PatchUpsell);
}
